import React, { useState, useEffect } from "react";
import VehicleInput from "../VehicleInput";
import { Settings, Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useApp } from "@/context/AppContext";
import { useAppConfig } from "@/hooks/useAppConfig";
import { chatService } from "@/services/ChatService";
import { settingsService } from "@/services/SettingsService";
import { VehicleData } from "@/types";
import "./EnhancedSidebar.css";

interface EnhancedSidebarProps {
  onSettingsClick?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  pageRoute?: string;
}

const EnhancedSidebar: React.FC<EnhancedSidebarProps> = ({
  onSettingsClick,
  isCollapsed = false,
  onToggleCollapse,
  pageRoute = "home",
}) => {
  const { addMessage, setLoading, setVehicleData, vehicleData } = useApp();
  const { getPageConfig, isSettingEnabled, appName } = useAppConfig();
  const [showVehicleInput, setShowVehicleInput] = useState(true);

  // Get page-specific configuration
  const pageConfig = getPageConfig(pageRoute);

  // Load settings on mount and listen for changes
  useEffect(() => {
    // Initial load
    const configVehicleInput = isSettingEnabled("vehicleInput");
    setShowVehicleInput(configVehicleInput);

    // Listen for settings updates
    const handleSettingsUpdate = () => {
      const currentConfigVehicleInput = isSettingEnabled("vehicleInput");
      setShowVehicleInput(currentConfigVehicleInput);
    };

    window.addEventListener("settings-updated", handleSettingsUpdate);

    return () => {
      window.removeEventListener("settings-updated", handleSettingsUpdate);
    };
  }, [isSettingEnabled]);

  // Get recent searches from config, fallback to default if not available
  const recentSearches = pageConfig?.recentSearches || [
    "Show me spark plug",
    "I'm looking for oil filter",
    "Brake pads",
    "Air filter",
    "Ignition Coil",
    "Serpentine Belt",
    "Alternator Assembly",
    "Strut Mount",
  ];

  // Get trending searches from config for additional suggestions
  const trendingSearches = pageConfig?.trendingSearches || [
    "Brake pads",
    "Oil filter",
    "Air filter",
    "Spark plugs",
    "Battery",
  ];

  // Config-based section visibility
  const showRecentSearches = recentSearches.length > 0;
  const showTrendingSearches = trendingSearches.length > 0;

  const handleVehicleSelect = (vehicleInfo: VehicleData) => {
    setVehicleData(vehicleInfo);
  };

  const handleRecentSearchClick = async (search: string) => {
    // Add user message
    addMessage({
      type: "user",
      content: search,
    });

    // Set loading state
    setLoading(true);

    try {
      // Call API
      const response = await chatService.searchParts(vehicleData, search);

      if (response.success) {
        if (response.responseType === 'markdown') {
          const responseMessage = chatService.generateChatResponse(
            [],
            [],
            search,
            undefined,
            response.markdownContent,
            'markdown'
          );

          addMessage({
            type: "assistant",
            content: responseMessage.content,
            responseType: responseMessage.responseType,
            markdownContent: responseMessage.markdownContent,
          });
        } else if (response.parts || response.vmrsParts) {
          console.log("🚀 ~ handleRecentSearchClick ~ response:", response)
          const responseMessage = chatService.generateChatResponse(
            response.parts || [],
            response.vmrsParts || [],
            search,
            response.pt,
            undefined,
            'json'
          );
          console.log("🚀 ~ handleRecentSearchClick ~ responseMessage:", responseMessage)

          addMessage({
            type: "assistant",
            content: responseMessage.content,
            pcdbParts: responseMessage.pcdbParts,
            vmrsParts: responseMessage.vmrsParts,
            pt: response.pt,
            responseType: responseMessage.responseType,
          });
        } else {
          addMessage({
            type: "assistant",
            content: 'Sorry, I couldn\'t find any parts for your search. Please try again with different terms.'
          });
        }
      } else {
        // Add error message
        addMessage({
          type: "assistant",
          content:
            response.error ||
            "Sorry, I couldn't find any parts for your search. Please try again with different terms.",
        });
      }
    } catch (error) {
      addMessage({
        type: "assistant",
        content:
          "Sorry, there was an error processing your request. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <aside className={cn("enhanced-sidebar", isCollapsed && "collapsed")}>
      {/* Fixed Header */}
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <div className="brand-logo">
            <img
              src="/plain_logo.png"
              alt="Predii Logo"
              className="logo-image"
            />
          </div>
          {!isCollapsed && (
            <div className="brand-text">
              <h1 className="brand-title">{appName} <sup>TM</sup></h1>
              <p className="brand-subtitle">{pageConfig?.agentLabel || 'Parts Agent'}</p>
            </div>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      {!isCollapsed && (
        <div className="sidebar-content">
          {/* Vehicle Selection - Conditionally Rendered */}
          {showVehicleInput && (
            <div className="sidebar-section">
              <h3 className="section-title">Vehicle Selection</h3>
              <div className="vehicle-input-container">
                <VehicleInput onVehicleSelect={handleVehicleSelect} />
              </div>
            </div>
          )}

          {/* Recent Searches */}
          {showRecentSearches && (
            <div className="sidebar-section">
              <h3 className="section-title">Recent Searches</h3>
              <div className="frequent-searches">
                {recentSearches.map((search, index) => (
                  <button
                    key={index}
                    className="search-suggestion-btn"
                    onClick={() => handleRecentSearchClick(search)}
                  >
                    {search}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Fixed Footer */}
      <div className="sidebar-footer">
        <button
          className="settings-btn"
          onClick={onSettingsClick}
          aria-label="Open settings"
        >
          <Settings size={18} />
          {!isCollapsed && <span>Settings</span>}
        </button>
      </div>
    </aside>
  );
};

export default EnhancedSidebar;
